import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CalendarDays, MapPin, Clock, Sparkles, Users, BadgeDollarSign } from 'lucide-react';
import axios from 'axios';
import { GIRLS_WINTER } from '@/data/content';
import { fadeUp, stagger, viewportOnce } from '@/lib/motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useGirlsSpots() {
  const [spots, setSpots] = useState(null);
  useEffect(() => {
    axios.get(`${API}/girls-spots`).then(r => setSpots(r.data.remaining)).catch(() => {});
  }, []);
  return spots;
}

export function spotsText(spots, fallback) {
  if (spots === null) return fallback;
  if (spots <= 0) return 'Program full — contact us for the waitlist';
  return `${spots} spot${spots === 1 ? '' : 's'} available — first come, first serve!`;
}

export default function GirlsWinter() {
  const g = GIRLS_WINTER;
  const spots = useGirlsSpots();
  return (
    <section id="girls-winter" className="relative py-24 sm:py-32 bg-night overflow-hidden" data-testid="girls-winter-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(230,57,70,0.1),transparent_55%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="flex items-center gap-4 mb-5">
          <span className="h-px w-12 bg-brand-gold/50" />
          <span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-brand-gold font-semibold flex items-center gap-2">
            <Sparkles size={14} /> New Program — Winter Season — Girls Only
          </span>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <motion.div
            variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={viewportOnce}
            className="relative"
          >
            <div className="rounded-sm overflow-hidden gold-hairline shadow-[0_0_60px_rgba(230,57,70,0.15)]">
              <img src={g.flyer} alt="Girls Winter Soccer Training — Abu Zama Soccer Academy" className="w-full object-cover" data-testid="girls-winter-flyer" />
            </div>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}>
            <motion.h2 variants={fadeUp} className="font-display uppercase tracking-tight leading-[0.95] text-5xl sm:text-6xl text-white">
              GIRLS <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-goldlight to-brand-gold">WINTER</span><br />SOCCER TRAINING
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="mt-5 text-base sm:text-lg text-gray-300 leading-relaxed max-w-lg">
              {g.blurb}
            </motion.p>

            <motion.div variants={fadeUp} custom={2} className="mt-7 space-y-3 text-sm sm:text-base" data-testid="girls-winter-details">
              <div className="flex items-center gap-3 text-gray-200"><CalendarDays size={17} className="text-brand-gold shrink-0" /><span className="font-semibold">{g.schedule}</span></div>
              <div className="flex items-center gap-3 text-gray-200"><MapPin size={17} className="text-brand-gold shrink-0" />{g.location}</div>
              <div className="flex items-center gap-3 text-gray-200"><Clock size={17} className="text-brand-gold shrink-0" />{g.period}</div>
              <div className="flex items-center gap-3 text-gray-200"><BadgeDollarSign size={17} className="text-brand-gold shrink-0" /><span><span className="font-bold text-white">{g.priceIntro}</span> — {g.paymentNote}</span></div>
              <div className="flex items-center gap-3"><Users size={17} className="text-brand-red shrink-0" /><span className="text-brand-red font-bold uppercase tracking-wide" data-testid="girls-winter-spots">{spotsText(spots, g.spots)}</span></div>
            </motion.div>

            <motion.div variants={fadeUp} custom={3} className="mt-6 flex flex-wrap gap-2">
              {g.focus.map(f => (
                <span key={f} className="gold-hairline rounded-full px-4 py-2 text-xs sm:text-sm text-brand-goldlight font-semibold uppercase tracking-widest">{f}</span>
              ))}
            </motion.div>

            <motion.p variants={fadeUp} custom={4} className="mt-6 font-display text-lg sm:text-xl tracking-[0.2em] text-gray-400 uppercase">{g.motto}</motion.p>

            <motion.div variants={fadeUp} custom={5} className="mt-8">
              <Link to="/girls-winter" data-testid="girls-winter-register-cta"
                className="inline-block bg-brand-red hover:bg-brand-redhover text-white text-center font-display tracking-widest text-xl sm:text-2xl px-10 py-4 rounded-sm transition-colors duration-300 shadow-[0_0_40px_rgba(230,57,70,0.35)]">
                GIRLS WINTER — FULL PAGE & REGISTRATION
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}
          className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
          data-testid="girls-winter-schedule"
        >
          {g.months.map((m, i) => (
            <motion.div key={m.month} variants={fadeUp} custom={i} className="glass-card rounded-sm p-5 hover:border-brand-gold/40 transition-[border-color] duration-300">
              <div className="font-display text-xl tracking-widest text-brand-gold uppercase">{m.month}</div>
              <div className="mt-3 text-xs uppercase tracking-[0.2em] text-gray-500 font-semibold">Practice Dates</div>
              <div className="mt-1 text-sm text-gray-300 leading-relaxed">{m.dates}</div>
              <div className="mt-3 text-xs uppercase tracking-[0.2em] text-gray-500 font-semibold">Payment Due</div>
              <div className="mt-1 text-sm font-bold text-white">{m.paymentDue}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
